import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import Landing2Footer from '../../components/Landing2Footer/Landing2Footer';
import Landing2Header from '../../components/Landing2Header/Landing2Header';
import CookieConsent from '../../components/CookieConsent/CookieConsent';
import NewsletterPopup from '../../components/NewsletterPopup/NewsletterPopup';
import { VENUE_LISTING_URL } from '../../components/Header/constants';
import blogPosts from '../../data/blogPosts';
import { BlogRow, useSharedBlogPhotoSize } from '../../components/Blog/BlogList';
import { usePageSeo } from '../../components/Seo/PageSeo';
import './Landing2.css';

const PERSONA_VENUES = 'venues';

const VENUES_HERO_TITLE_LINES = [
  { words: ['Turn', 'your', 'quiet', 'hours'], muted: true },
  { words: ['into', 'income'], muted: false },
];

const HERO_TITLE_WORD_START = 0.12;
const HERO_TITLE_WORD_STAGGER = 0.1;

const VENUE_TESTIMONIAL_GRID_GAP = 22;
const VENUE_TESTIMONIAL_GRID_RADIUS = 3.2;
const VENUE_TESTIMONIAL_GRID_BURST_MS = 200;
const VENUE_TESTIMONIAL_GRID_HOLD = 1.05;
const VENUE_TESTIMONIAL_GRID_RING = [
  { max: 0.85, color: '#046242' },
  { max: 1.85, color: '#BED347' },
  { max: VENUE_TESTIMONIAL_GRID_RADIUS, color: '#E4FD58' },
];

const VENUE_TESTIMONIALS = [
  {
    author: 'Krishana',
    event: 'Kasa Cafe, London',
    quote:
      'We were the first venue on Occasia, so we got to shape it. When something didn’t fit how our day actually runs, we said so, and it changed. Event enquiries used to arrive as Instagram DMs and WhatsApp messages. Now they land in one place with the date, numbers and payment already sorted. We spend our time hosting instead of chasing enquiries.',
    avatar: '/landing2/testimonials/krishana.jpg',
    avatarPosition: '50% 28%',
  },
  {
    author: 'Kim',
    event: 'Kiju Coffee House',
    quote:
      'It took us about a week to get the hang of it. When the free trial ended, keeping it was an easy decision. All our bookings are in the same calendar on the platform now. No more checking for availability by frantically looking across a notebook, a message thread on WhatsApp or on Instagram DMs before we say yes to it.',
    avatar: '/landing2/testimonials/kim.png',
    avatarPosition: '50% 20%',
  },
];

const VENUE_BUILT_FOR_SCENES = [
  {
    title: 'Neighbourhood spaces',
    titleLine2: 'ready to host',
    names: ['Cafés', 'Bars', 'Pubs'],
    image: '/landing2/venue-types/cafes-bars-pubs.jpg',
  },
  {
    title: 'Rooms that work',
    titleLine2: 'after hours',
    names: ['Coworking spaces', 'Studios', 'Training rooms'],
    image: '/landing2/venue-types/coworking-studios.jpg',
  },
  {
    title: 'Characterful rooms',
    titleLine2: 'with a story',
    names: ['Galleries', 'Creative spaces', 'Community rooms'],
    image: '/landing2/venue-types/galleries-creative.jpg',
  },
];

const Landing2 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const personaParam = searchParams.get('persona');
  const persona = PERSONA_VENUES;

  useEffect(() => {
    if (personaParam === 'events' || personaParam === PERSONA_VENUES) return;
    setSearchParams({ persona: PERSONA_VENUES }, { replace: true });
  }, [personaParam, setSearchParams]);

  usePageSeo({
    title: 'List your venue on Occasia',
    description:
      'Turn quiet hours into income. Occasia helps pubs, cafés, and event spaces take enquiries, payments, and bookings in one place — built with venue partners in the UK.',
    path: '/onboard',
    keywords:
      'list your venue, venue hire platform UK, event space marketing, pub private hire, Occasia for venues'
  });

  const handlePersonaChange = (nextPersona) => {
    if (nextPersona === 'events') {
      navigate('/');
      return;
    }
    setSearchParams({ persona: nextPersona }, { replace: true });
  };

  const [venueTestimonialIndex, setVenueTestimonialIndex] = useState(0);
  const venueTestimonialsGridRef = useRef(null);
  const venueBuiltForRef = useRef(null);
  const journalPostsRef = useRef(null);
  useSharedBlogPhotoSize(journalPostsRef);
  const featuredPosts = useMemo(() => {
    if (!blogPosts || blogPosts.length === 0) return [];
    const shuffled = [...blogPosts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  }, []);
  const [dashboardIndex, setDashboardIndex] = useState(0);
  const [dashboardIsMobile, setDashboardIsMobile] = useState(false);
  const dashboardMaxIndex = dashboardIsMobile ? 3 : 2;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => {
      const mobile = mq.matches;
      setDashboardIsMobile(mobile);
      if (!mobile) {
        setDashboardIndex((index) => Math.min(index, 2));
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const scrollDashboard = useCallback((direction) => {
    setDashboardIndex((index) => {
      const next = index + direction;
      if (next < 0 || next > dashboardMaxIndex) return index;
      return next;
    });
  }, [dashboardMaxIndex]);

  const goVenueTestimonial = useCallback((direction) => {
    setVenueTestimonialIndex((index) => {
      const next = direction === 'next' ? index + 1 : index - 1;
      if (next < 0 || next >= VENUE_TESTIMONIALS.length) return index;
      return next;
    });
  }, []);

  useEffect(() => {
    const page = document.querySelector('.l2-page');
    let frameId = 0;

    const updateNight = () => {
      const section = venueBuiltForRef.current;
      if (!section || !page) return;
      const header = document.querySelector('.l2-header-bar');
      const headerBottom = header?.getBoundingClientRect().bottom ?? 64;
      const rect = section.getBoundingClientRect();
      page.classList.toggle('l2-page--night', rect.top < headerBottom && rect.bottom > 0);
    };

    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updateNight();
      });
    };

    updateNight();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.cancelAnimationFrame(frameId);
      page?.classList.remove('l2-page--night');
    };
  }, []);

  useEffect(() => {
    const canvas = venueTestimonialsGridRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const clusters = [];
    let cols = 0;
    let rows = 0;
    let frameId = 0;
    let lastBurst = 0;

    const spawnBurst = () => {
      const count = 3 + Math.floor(Math.random() * 2);
      const margin = Math.ceil(VENUE_TESTIMONIAL_GRID_RADIUS) + 1;
      for (let i = 0; i < count; i += 1) {
        clusters.push({
          col: margin + Math.floor(Math.random() * Math.max(1, cols - margin * 2)),
          row: margin + Math.floor(Math.random() * Math.max(1, rows - margin * 2)),
          born: performance.now(),
        });
      }
    };

    const ringColor = (distance) => {
      const ring = VENUE_TESTIMONIAL_GRID_RING.find((item) => distance <= item.max);
      return ring?.color ?? '#E4FD58';
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / VENUE_TESTIMONIAL_GRID_GAP);
      rows = Math.ceil(height / VENUE_TESTIMONIAL_GRID_GAP);
      clusters.length = 0;
      lastBurst = 0;
    };

    const draw = (now) => {
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      ctx.clearRect(0, 0, width, height);

      if (now - lastBurst >= VENUE_TESTIMONIAL_GRID_BURST_MS) {
        spawnBurst();
        lastBurst = now;
      }

      const hold = VENUE_TESTIMONIAL_GRID_HOLD * 1000;
      for (let i = clusters.length - 1; i >= 0; i -= 1) {
        if (now - clusters[i].born >= hold) clusters.splice(i, 1);
      }

      const lit = new Map();
      clusters.forEach((cluster) => {
        const intensity = Math.sin(((now - cluster.born) / hold) * Math.PI);
        const extent = Math.ceil(VENUE_TESTIMONIAL_GRID_RADIUS);
        for (let dy = -extent; dy <= extent; dy += 1) {
          for (let dx = -extent; dx <= extent; dx += 1) {
            const distance = Math.hypot(dx, dy);
            if (distance > VENUE_TESTIMONIAL_GRID_RADIUS) continue;
            const key = `${cluster.col + dx},${cluster.row + dy}`;
            const prev = lit.get(key);
            if (!prev || intensity > prev.intensity) {
              lit.set(key, { color: ringColor(distance), intensity });
            }
          }
        }
      });

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const litDot = lit.get(`${col},${row}`);
          ctx.beginPath();
          ctx.arc(
            col * VENUE_TESTIMONIAL_GRID_GAP,
            row * VENUE_TESTIMONIAL_GRID_GAP,
            1.05,
            0,
            Math.PI * 2
          );
          if (litDot) {
            ctx.globalAlpha = 0.08 + litDot.intensity * 0.92;
            ctx.fillStyle = litDot.color;
          } else {
            ctx.globalAlpha = 1;
            ctx.fillStyle = 'rgba(17, 17, 17, 0.07)';
          }
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      frameId = window.requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    frameId = window.requestAnimationFrame(draw);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  if (personaParam === 'events') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="l2-page">
      <div className="l2-header-shell">
        <Landing2Header
          variant="light"
          showPersonaSwitcher
          persona={persona}
          onPersonaChange={handlePersonaChange}
        />
      </div>
      {/* ===== Hero ===== */}
      <section className="l2-hero l2-hero--venues">
        <div className="l2-hero-ribbon" aria-hidden>
          <svg className="l2-hero-ribbon-svg" viewBox="0 0 1600 240" preserveAspectRatio="none">
            <defs>
              <clipPath id="l2RibbonClip">
                <rect width="1600" height="240" />
              </clipPath>
              <linearGradient id="l2RibbonGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0a3226" />
                <stop offset="18%" stopColor="#046242" />
                <stop offset="38%" stopColor="#BED347" />
                <stop offset="50%" stopColor="#E4FD58" />
                <stop offset="62%" stopColor="#BED347" />
                <stop offset="82%" stopColor="#046242" />
                <stop offset="100%" stopColor="#0a3226" />
                <animate attributeName="x1" values="-0.4;0.2;-0.4" dur="18s" repeatCount="indefinite" />
                <animate attributeName="x2" values="0.6;1.2;0.6" dur="18s" repeatCount="indefinite" />
              </linearGradient>
              <linearGradient id="l2RibbonEdge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
                <stop offset="18%" stopColor="#ffffff" stopOpacity="0" />
                <stop offset="82%" stopColor="#000000" stopOpacity="0" />
                <stop offset="100%" stopColor="#0a3226" stopOpacity="0.28" />
              </linearGradient>
              <filter id="l2RibbonWave" x="-25%" y="-40%" width="150%" height="180%">
                <feTurbulence
                  type="turbulence"
                  baseFrequency="0.0018 0.0026"
                  numOctaves="2"
                  seed="8"
                  result="noise"
                >
                  <animate
                    attributeName="baseFrequency"
                    values="0.0016 0.0022;0.0024 0.0034;0.0016 0.0022"
                    dur="22s"
                    repeatCount="indefinite"
                  />
                </feTurbulence>
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="noise"
                  scale="120"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </defs>
            <g clipPath="url(#l2RibbonClip)">
              <rect
                x="-160"
                y="-120"
                width="1920"
                height="480"
                fill="url(#l2RibbonGrad)"
                filter="url(#l2RibbonWave)"
              />
              <rect width="1600" height="240" fill="url(#l2RibbonEdge)" />
            </g>
          </svg>
        </div>
        <div className="l2-hero-content">
          <h1 className="l2-hero-title">
            {VENUES_HERO_TITLE_LINES.map((line, lineIndex) => {
              const wordOffset = VENUES_HERO_TITLE_LINES.slice(0, lineIndex).reduce(
                (count, item) => count + item.words.length,
                0
              );
              return (
                <span
                  key={line.words.join('-')}
                  className={`l2-hero-title-line${line.muted ? '' : ' l2-hero-highlight'}`}
                >
                  {line.words.map((word, wordIndex) => (
                    <span
                      key={`${word}-${wordIndex}`}
                      className="l2-hero-word"
                      style={{
                        animationDelay: `${HERO_TITLE_WORD_START + (wordOffset + wordIndex) * HERO_TITLE_WORD_STAGGER}s`,
                      }}
                    >
                      {word}
                    </span>
                  ))}
                </span>
              );
            })}
          </h1>
          <p className="l2-hero-subtitle">
            List your space on Occasia and get discovered by small event organisers looking for local, characterful venues.
          </p>
          <a
            href={VENUE_LISTING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="l2-hero-cta-btn"
          >
            List your venue
          </a>
        </div>
      </section>


      {/* ===== VENUES CONTENT ===== */}
      <>
          {/* Features */}
          <section className="l2-v-features">
            <div className="l2-v-features-layout">
              <div className="l2-v-features-header">
                <h2 className="l2-v-features-title">
                  <span className="l2-v-features-title-line l2-v-features-title-line--hl">Make more</span>
                  <span className="l2-v-features-title-line">from the space you already have</span>
                </h2>
              </div>
              <div className="l2-v-features-steps">
                {[
                  {
                    icon: '/landing2/features/flash.png',
                    title: 'Free & fast to set up',
                    desc: (
                      <>
                        Add your space, set your pricing, and start receiving bookings.
                        <br />
                        No technical setup. No onboarding call.
                      </>
                    ),
                  },
                  {
                    icon: '/landing2/features/receipt.png',
                    title: 'We bring you bookings',
                    desc: (
                      <>
                        We match your space against real event briefs and connect you
                        <br />
                        with organisers who are ready to book.
                      </>
                    ),
                  },
                  {
                    icon: '/landing2/features/settings.png',
                    title: "You're always in control",
                    desc: (
                      <>
                        Every request comes with full event details upfront.
                        <br />
                        You decide what fits, we handle the rest.
                      </>
                    ),
                  },
                  {
                    icon: '/landing2/features/chart.png',
                    title: 'Low effort, high upside',
                    desc: (
                      <>
                        Payments, confirmations, and organiser communication all in one place.
                        <br />
                        Your calendar fills itself.
                      </>
                    ),
                  },
                ].map((item) => (
                  <div className="l2-v-feature" key={item.title}>
                    <img src={item.icon} alt="" className="l2-v-feature-icon" aria-hidden="true" />
                    <div className="l2-v-feature-rule" aria-hidden />
                    <div className="l2-v-feature-copy">
                      <h3 className="l2-v-feature-title">{item.title}</h3>
                      <p className="l2-v-feature-desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Dashboard showcase */}
          <section className="l2-v-dashboard">
            <div className="l2-v-dashboard-header">
              <div className="l2-v-dashboard-header-copy">
                <h2 className="l2-v-dashboard-title">
                  The simple dashboard that does<br /> the heavy lifting
                </h2>
                <p className="l2-v-dashboard-subtitle">
                  Occasia comes with an easy-to-use dashboard that keeps bookings, requests, and details in one place.
                </p>
              </div>
              <div className="l2-v-dashboard-nav">
                <button
                  type="button"
                  className="l2-v-dashboard-nav-btn"
                  aria-label="Previous dashboard cards"
                  disabled={dashboardIndex === 0}
                  onClick={() => scrollDashboard(-1)}
                >
                  <img src="/manual-booking/icons/arrow-left.svg" alt="" aria-hidden />
                </button>
                <button
                  type="button"
                  className="l2-v-dashboard-nav-btn"
                  aria-label="Next dashboard cards"
                  disabled={dashboardIndex >= dashboardMaxIndex}
                  onClick={() => scrollDashboard(1)}
                >
                  <img src="/manual-booking/icons/arrow-right-nav.svg" alt="" aria-hidden />
                </button>
              </div>
            </div>
            <div className="l2-v-dashboard-viewport">
            <div
              className={`l2-v-dashboard-content${dashboardIndex >= dashboardMaxIndex ? ' is-end' : ''}`}
              style={{ '--l2-dash-index': dashboardIndex }}
            >
              <article className="l2-v-dashboard-card">
                <div className="l2-v-dashboard-card-text">
                  <h3 className="l2-v-dashboard-card-title">One calendar for everything</h3>
                  <p className="l2-v-dashboard-card-desc">See all your Occasia bookings and availability together</p>
                </div>
                <div className="l2-v-dashboard-visual l2-v-dashboard-visual--calendar">
                  <div className="l2-v-dashboard-snapshot l2-v-dashboard-snapshot--calendar">
                    <img src="/landing2/dashboard/ellipse-257.svg" alt="" className="l2-v-dashboard-bg-orb" aria-hidden="true" />
                    <img src="/landing2/dashboard/calendar4.png" alt="Occasia calendar dashboard view" className="l2-v-dashboard-shot l2-v-dashboard-shot--calendar" />
                  </div>
                </div>
              </article>

              <article className="l2-v-dashboard-card">
                <div className="l2-v-dashboard-card-text">
                  <h3 className="l2-v-dashboard-card-title">All organiser details upfront</h3>
                  <p className="l2-v-dashboard-card-desc">Event type, people, timing, and requirements first</p>
                </div>
                <div className="l2-v-dashboard-visual l2-v-dashboard-visual--details">
                  <div className="l2-v-dashboard-snapshot l2-v-dashboard-snapshot--details">
                    <img src="/landing2/dashboard/ellipse-258.svg" alt="" className="l2-v-dashboard-bg-orb" aria-hidden="true" />
                    <img src="/landing2/dashboard/booking-details-1.png" alt="Booking details panel" className="l2-v-dashboard-shot l2-v-dashboard-shot--stack l2-v-dashboard-shot--one" />
                    <img src="/landing2/dashboard/event-2.png" alt="Event requirements panel" className="l2-v-dashboard-shot l2-v-dashboard-shot--stack l2-v-dashboard-shot--two" />
                    <img src="/landing2/dashboard/customer-2.png" alt="Customer details panel" className="l2-v-dashboard-shot l2-v-dashboard-shot--stack l2-v-dashboard-shot--three" />
                  </div>
                </div>
              </article>

              <article className="l2-v-dashboard-card">
                <div className="l2-v-dashboard-card-text">
                  <h3 className="l2-v-dashboard-card-title">Accept, decline, or ask questions</h3>
                  <p className="l2-v-dashboard-card-desc">Handle requests in a couple of clicks</p>
                </div>
                <div className="l2-v-dashboard-visual l2-v-dashboard-visual--decision">
                  <div className="l2-v-dashboard-snapshot l2-v-dashboard-snapshot--single">
                    <img src="/landing2/dashboard/ellipse-259.svg" alt="" className="l2-v-dashboard-bg-orb" aria-hidden="true" />
                    <img src="/landing2/dashboard/event-3.png" alt="Booking confirmation actions" className="l2-v-dashboard-shot l2-v-dashboard-shot--single" />
                  </div>
                </div>
              </article>

              <article className="l2-v-dashboard-card">
                <div className="l2-v-dashboard-card-text">
                  <div className="l2-v-dashboard-card-title-row">
                    <h3 className="l2-v-dashboard-card-title">Insights that help you earn more</h3>
                    <span className="l2-v-dashboard-coming-soon">Coming soon</span>
                  </div>
                  <p className="l2-v-dashboard-card-desc">See which days, times, and event types perform best</p>
                </div>
                <div className="l2-v-dashboard-visual l2-v-dashboard-visual--insights">
                  <div className="l2-v-dashboard-snapshot l2-v-dashboard-snapshot--single">
                    <img src="/landing2/dashboard/ellipse-260.svg" alt="" className="l2-v-dashboard-bg-orb" aria-hidden="true" />
                    <img src="/landing2/dashboard/event-4.png" alt="Analytics preview" className="l2-v-dashboard-shot l2-v-dashboard-shot--single" />
                  </div>
                </div>
              </article>
            </div>
            </div>
          </section>
      </>

      <>
          {/* ===== VENUES: Section (Figma node 2246-2817) ===== */}
          <section className="l2-v-how-figma">
            <div className="l2-v-how-figma-inner">
              <div className="l2-v-how-figma-top">
                <h2 className="l2-v-how-figma-title">How it works</h2>
                <div className="l2-v-how-figma-steps">
                  {[
                    { num: '01.', title: 'Tell us about your space', desc: "Add photos, capacity, amenities, pricing style, and the hours you're open to hosting." },
                    { num: '02.', title: 'Organisers discover you', desc: 'Your venue shows up on Occasia where organisers browse and compare spaces.' },
                    { num: '03.', title: 'Get booking requests', desc: 'Organisers see all your details upfront and reach out with requests that match your setup.' },
                    { num: '04.', title: 'You choose what works', desc: "Accept the bookings you want. Decline the ones you don't." },
                    { num: '05.', title: 'Earn extra revenue', desc: "Host great events during hours that might've gone unused." },
                  ].map((item) => (
                    <div key={item.num} className="l2-v-how-figma-step">
                      <span className="l2-v-how-figma-num">{item.num}</span>
                      <div className="l2-v-how-figma-step-text">
                        <h3 className="l2-v-how-figma-step-title">{item.title}</h3>
                        <p className="l2-v-how-figma-step-desc">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="l2-v-built-for" ref={venueBuiltForRef}>
            <div className="l2-v-built-for-stack">
              <h2 className="l2-v-built-for-title">Who we&apos;re built for</h2>
              <p className="l2-v-built-for-subtitle">
                If your space can host 5 to 50 people, you&apos;ll probably feel
                <br />
                right at home here.
              </p>
              <div className="l2-v-built-for-cta-row">
                <a
                  href={VENUE_LISTING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="l2-v-built-for-cta"
                >
                  List your venue
                </a>
              </div>
              <div className="l2-v-built-for-cards">
                {VENUE_BUILT_FOR_SCENES.map((scene) => (
                  <article className="l2-v-built-for-card" key={scene.title}>
                    <div className="l2-v-built-for-card-media" aria-hidden>
                      <img src={scene.image} alt="" />
                    </div>
                    <div className="l2-v-built-for-card-left">
                      <h3 className="l2-v-built-for-card-title">
                        {scene.title}
                        {scene.titleLine2 ? (
                          <>
                            <br />
                            {scene.titleLine2}
                          </>
                        ) : null}
                      </h3>
                    </div>
                    <ul className="l2-v-built-for-card-names">
                      {scene.names.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="l2-new-section-1">
            <div className="l2-new-section-1-grid" aria-hidden>
              <canvas ref={venueTestimonialsGridRef} className="l2-new-section-1-grid-dots" />
            </div>
            <div className="l2-new-section-1-inner">
              <div className="l2-new-section-1-header">
                <h2 className="l2-new-section-1-title">
                  Spaces coming alive with
                  <br />
                  new energy
                </h2>
              </div>
              <div className="l2-new-section-1-carousel">
                <div className="l2-new-section-1-frame">
                  <span className="l2-new-section-1-frame-rule l2-new-section-1-frame-rule--top" aria-hidden />
                  <span className="l2-new-section-1-frame-rule l2-new-section-1-frame-rule--bottom" aria-hidden />
                  <span className="l2-new-section-1-corner l2-new-section-1-corner--tl" aria-hidden />
                  <span className="l2-new-section-1-corner l2-new-section-1-corner--tr" aria-hidden />
                  <button
                    type="button"
                    className="l2-new-section-1-nav l2-new-section-1-nav--prev"
                    aria-label="Previous testimonial"
                    disabled={venueTestimonialIndex === 0}
                    onClick={() => goVenueTestimonial('prev')}
                  >
                    <img src="/manual-booking/icons/arrow-left.svg" alt="" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="l2-new-section-1-nav l2-new-section-1-nav--next"
                    aria-label="Next testimonial"
                    disabled={venueTestimonialIndex === VENUE_TESTIMONIALS.length - 1}
                    onClick={() => goVenueTestimonial('next')}
                  >
                    <img src="/manual-booking/icons/arrow-right-nav.svg" alt="" aria-hidden />
                  </button>
                  <div className="l2-new-section-1-track">
                    <div className="l2-new-section-1-quotes">
                      {VENUE_TESTIMONIALS.map((item, index) => (
                        <p
                          key={item.author}
                          className={`l2-new-section-1-quote${index === venueTestimonialIndex ? ' is-active' : ''}`}
                        >
                          &ldquo;{item.quote}&rdquo;
                        </p>
                      ))}
                    </div>
                    <div className="l2-new-section-1-faces">
                      <div
                        className="l2-new-section-1-faces-track"
                        style={{
                          transform: `translateX(calc(var(--l2-face-slot) * ${1 - venueTestimonialIndex}))`,
                        }}
                      >
                        {VENUE_TESTIMONIALS.map((item, index) => (
                          <button
                            key={item.author}
                            type="button"
                            className={`l2-new-section-1-avatar-wrap${index === venueTestimonialIndex ? ' is-active' : ''}`}
                            aria-label={`${item.author}’s testimonial`}
                            aria-pressed={index === venueTestimonialIndex}
                            onClick={() => setVenueTestimonialIndex(index)}
                          >
                            <img
                              src={item.avatar}
                              alt=""
                              className={`l2-new-section-1-avatar${index === venueTestimonialIndex ? ' is-active' : ''}`}
                              style={item.avatarPosition ? { objectPosition: item.avatarPosition } : undefined}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="l2-new-section-1-bylines">
                      {VENUE_TESTIMONIALS.map((item, index) => (
                        <div
                          key={item.author}
                          className={`l2-new-section-1-details${index === venueTestimonialIndex ? ' is-active' : ''}`}
                        >
                          <p className="l2-new-section-1-author">{item.author}</p>
                          <h3 className="l2-new-section-1-event">{item.event}</h3>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
      </>

      {/* ===== SHARED: FAQ ===== */}
      <section className="l2-faq">
        <div className="l2-faq-inner">
          <h2 className="l2-faq-title">
            <span className="l2-faq-title-line">Got questions?</span>
            <span className="l2-faq-title-line l2-faq-title-line--hl">We&apos;ve got answers</span>
          </h2>
          <div className="l2-faq-panel">
            <div className="l2-faq-rule" aria-hidden />
            <div className="l2-faq-list">
              {[
                { q: 'Is it free to list my space?', a: 'Yes, listing is completely free. No subscriptions, no sign-up fees. We only take a small commission when you get a booking.' },
                { q: 'What kind of spaces can I list?', a: 'Cafés, bars, restaurants, studios, galleries, community halls — any characterful space that can host small events.' },
                { q: 'How do I manage bookings?', a: 'Through your dashboard. You can accept, decline, or message organisers directly. It works on any device.' },
                { q: 'How quickly can I start?', a: 'Most venues are live within 24 hours. Add your photos, set your availability, and you\'re ready to go.' },
              ].map((item) => (
                <details key={item.q} className="l2-faq-item">
                  <summary className="l2-faq-question">
                    {item.q}
                    <img src="/manual-booking/icons/faq-add.png" alt="" className="l2-faq-icon l2-faq-icon--closed" aria-hidden />
                    <img src="/manual-booking/icons/faq-minus.png" alt="" className="l2-faq-icon l2-faq-icon--open" aria-hidden />
                  </summary>
                  <p className="l2-faq-answer">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="l2-blog">
        <div className="l2-blog-heading">
          <h2 className="l2-blog-title">From the Occasia journal</h2>
          <p className="l2-blog-subtitle">Stories, tips, and things we're learning</p>
        </div>
        <Link to="/blog" className="l2-blog-explore">Explore our journal</Link>
        <div className="l2-blog-divider" aria-hidden />
        <div className="l2-blog-posts" ref={journalPostsRef}>
          {featuredPosts.map((post) => (
            <BlogRow post={post} key={post.slug} />
          ))}
        </div>
      </section>

      <Landing2Footer />
      <CookieConsent />
      <NewsletterPopup />
    </div>
  );
};

export default Landing2;
